import styled from '@emotion/styled';
import { Stack } from '@mui/material';
import type { ReactNode } from 'react';

import { TransactionViewDirection } from '@/api/transaction-views/queries';
import ExternalLinkIcon from '@/assets/svg/ui-elements/external-link.svg';
import ViewIcon from '@/assets/svg/ui-elements/eye-outline.svg';
import NotesIcon from '@/assets/svg/ui-elements/note.svg';
import IncomingIcon from '@/assets/svg/ui-elements/receive.svg';
import OutgoingIcon from '@/assets/svg/ui-elements/send.svg';
import TwoWayIcon from '@/assets/svg/ui-elements/swap.svg';
import { getSuiExplorerAddressUrl, getSuiExplorerTransactionUrl } from '@/config/sui';
import { formatTransactionType } from '@/features/transactions/utils/format-transaction-type';
import { formatSignedAmount } from '@/features/transactions/utils/transaction-detail';
import {
  formatTransactionDate,
  formatTransactionDirection,
  formatTransactionUsdValue,
  type TransactionListCoin,
  type TransactionListGas,
  type TransactionListItem,
  type TransactionListLine,
} from '@/features/transactions/utils/transaction-list';
import {
  IconButton,
  Link,
  type LinkProps,
  theme,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import { typographyVariantStyles } from '@/shared/design-system/typography-variants';
import { truncateAddress } from '@/utils/truncate-address';

import { CoinIcon } from '../crypto-assets/coin-icon';

type SubLineVariant = 'body2' | 'body3';

const MAX_SUB_LINES = 3;

const directionIcons = {
  [TransactionViewDirection.Incoming]: IncomingIcon,
  [TransactionViewDirection.Outgoing]: OutgoingIcon,
  [TransactionViewDirection.TwoWay]: TwoWayIcon,
};

type TransactionExternalLinkProps = Extract<LinkProps, { href: string }>;

const TransactionExternalLinkBase = (props: TransactionExternalLinkProps) => <Link {...props} />;

const TransactionAddressLink = styled(TransactionExternalLinkBase, {
  shouldForwardProp: (property) => property !== '$compact',
})<{ $compact: boolean }>(({ theme, $compact }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  padding: theme.spacing[0],
  gap: theme.spacing[4],
  overflow: 'hidden',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...($compact ? typographyVariantStyles.body3 : {}),
}));

const TransactionLinkText = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const TransactionCounterpartyText = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  minWidth: 0,
  maxWidth: '100%',
  gap: theme.spacing[4],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const TransactionCounterpartyName = styled(TransactionLinkText)({
  flex: '1 1 auto',
});

const TransactionCounterpartyAddress = styled(TransactionLinkText)(({ theme }) => ({
  flex: '0 1 auto',
  color: theme.colors.text.secondary,
}));

const TransactionExplorerIcon = styled(ExternalLinkIcon)({
  flexShrink: 0,
});

const TransactionExplorerLink = styled(TransactionExternalLinkBase)(({ theme }) => ({
  display: 'inline-flex',
  minWidth: 0,
  maxWidth: '100%',
  padding: theme.spacing[0],
  gap: theme.spacing[4],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const NotesTrigger = styled('span')({
  display: 'inline-flex',
});

const TransactionSubLineStack = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  gap: theme.spacing[4],
  '& > *': {
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
  },
}));

const TransactionSubLines = ({
  items,
  variant,
}: {
  items: ReactNode[];
  variant: SubLineVariant;
}) => {
  if (items.length === 0) {
    return (
      <Typography as="span" variant={variant} fontWeight={500}>
        —
      </Typography>
    );
  }
  const shownItems = items.length > MAX_SUB_LINES ? items.slice(0, MAX_SUB_LINES - 1) : items;
  const hiddenCount = items.length - shownItems.length;
  return (
    <TransactionSubLineStack>
      {shownItems}
      {hiddenCount > 0 ? (
        <Typography as="span" variant={variant} color={theme.colors.text.secondary} noWrap>
          {`+${hiddenCount} more`}
        </Typography>
      ) : null}
    </TransactionSubLineStack>
  );
};

export const TransactionDirectionCell = ({ transaction }: TransactionCellProps) => {
  const DirectionIcon = transaction.direction ? directionIcons[transaction.direction] : null;
  return (
    <Stack direction="row" alignItems="center" gap={`${theme.spacing[8]}px`}>
      {DirectionIcon ? <DirectionIcon width={24} height={24} aria-hidden="true" /> : null}
      <Typography as="span" variant="body2" fontWeight={500}>
        {formatTransactionDirection(transaction.direction)}
      </Typography>
    </Stack>
  );
};

export const TransactionCounterpartyCell = ({ transaction }: TransactionCellProps) => {
  if (!transaction.lines.some((line) => line.counterpartyLabel !== '—')) {
    return null;
  }

  const variant = transaction.lines.length > 1 ? 'body3' : 'body2';
  return (
    <TransactionSubLines
      variant={variant}
      items={transaction.lines.map((line, index) => (
        <TransactionCounterpartyLine key={index} line={line} variant={variant} />
      ))}
    />
  );
};

const TransactionCounterpartyLine = ({ line, variant }: TransactionLineProps) => {
  if (line.counterpartyLabel === '—') {
    return (
      <Typography as="span" variant={variant} aria-hidden="true">
        &nbsp;
      </Typography>
    );
  }

  const address = line.counterpartyAddress ?? line.counterpartyLabel;
  const label = line.counterpartyAddress
    ? truncateAddress(line.counterpartyAddress)
    : line.counterpartyLabel;
  const tooltip = line.counterpartyDisplayName
    ? `${line.counterpartyDisplayName} (${address})`
    : line.counterpartyLabel;
  const content = line.counterpartyDisplayName ? (
    <TransactionCounterpartyText>
      <TransactionCounterpartyName>{line.counterpartyDisplayName}</TransactionCounterpartyName>
      <TransactionCounterpartyAddress>{label}</TransactionCounterpartyAddress>
    </TransactionCounterpartyText>
  ) : (
    <TransactionLinkText>{label}</TransactionLinkText>
  );

  return (
    <Tooltip title={tooltip} align="start" disabled={tooltip.length < 20}>
      {line.counterpartyAddress ? (
        <TransactionAddressLink
          href={getSuiExplorerAddressUrl(line.counterpartyAddress)}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          aria-label={address}
          $compact={variant === 'body3'}
          onClick={(event) => event.stopPropagation()}
        >
          {content}
          <TransactionExplorerIcon width={12} height={12} aria-hidden="true" />
        </TransactionAddressLink>
      ) : (
        <Typography as="span" variant={variant} fontWeight={500} noWrap>
          {content}
        </Typography>
      )}
    </Tooltip>
  );
};

export const TransactionDateCell = ({ transaction }: TransactionCellProps) => (
  <Typography as="span" variant="body2" fontWeight={500}>
    {formatTransactionDate(transaction.date)}
  </Typography>
);

export const TransactionAmountCell = ({ transaction }: TransactionCellProps) => {
  const variant: SubLineVariant = 'body2';
  const gasItems = transaction.gas
    ? [<TransactionGasAmountLine key="gas" gas={transaction.gas} variant={variant} />]
    : [];
  return (
    <TransactionSubLines
      variant={variant}
      items={[
        ...transaction.lines.map((line, index) => (
          <TransactionAmountLine key={index} line={line} variant={variant} />
        )),
        ...gasItems,
      ]}
    />
  );
};

const TransactionUsdValue = ({ value }: { value: string | null }) => {
  const amountInUsd = formatTransactionUsdValue(value);
  return amountInUsd ? (
    <Typography as="span" variant="body3" color={theme.colors.text.secondary} noWrap>
      ({amountInUsd})
    </Typography>
  ) : null;
};

const TransactionAmountLine = ({ line, variant }: TransactionLineProps) => (
  <Stack direction="row" alignItems="center" gap={`${theme.spacing[8]}px`} minWidth={0}>
    <Typography as="span" variant={variant} fontWeight={500} noWrap>
      {formatSignedAmount(line.amount)}
    </Typography>
    <TransactionUsdValue value={line.amountInUsd} />
  </Stack>
);

const TransactionGasAmountLine = ({
  gas,
  variant,
}: {
  gas: TransactionListGas;
  variant: SubLineVariant;
}) => (
  <Stack direction="row" alignItems="center" gap={`${theme.spacing[8]}px`} minWidth={0}>
    <Typography as="span" variant={variant} color={theme.colors.text.secondary} noWrap>
      {gas.amountLabel}
    </Typography>
    <Typography as="span" variant="body3" color={theme.colors.text.secondary} noWrap>
      {gas.payerLabel}
    </Typography>
  </Stack>
);

export const TransactionDenominationCell = ({ transaction }: TransactionCellProps) => {
  const variant: SubLineVariant = 'body2';
  const gasItems = transaction.gas
    ? [
        <TransactionCoinLine
          key="gas"
          coin={transaction.gas.coin}
          denomination={transaction.gas.coin?.symbol ?? null}
          variant={variant}
        />,
      ]
    : [];
  return (
    <TransactionSubLines
      variant={variant}
      items={[
        ...transaction.lines.map((line, index) => (
          <TransactionCoinLine
            key={index}
            coin={line.coin}
            denomination={line.denomination}
            variant={variant}
          />
        )),
        ...gasItems,
      ]}
    />
  );
};

const TransactionCoinLine = ({
  coin,
  denomination,
  variant,
}: {
  coin: TransactionListCoin | null;
  denomination: string | null;
  variant: SubLineVariant;
}) => (
  <Stack direction="row" alignItems="center" gap={`${theme.spacing[8]}px`}>
    {coin ? <CoinIcon symbol={coin.symbol} iconUrl={coin.iconUrl} size={28} /> : null}
    <Typography as="span" variant={variant} fontWeight={500}>
      {denomination ?? '—'}
    </Typography>
  </Stack>
);

export const TransactionTypeCell = ({ transaction }: TransactionCellProps) => (
  <Typography as="span" variant="body2" fontWeight={500}>
    {formatTransactionType(transaction.type)}
  </Typography>
);

export const TransactionActionsCell = ({
  transaction,
  onSelect,
}: TransactionCellProps & { onSelect: (_id: string) => void }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="flex-end"
    gap={`${theme.spacing[4]}px`}
    minWidth={0}
  >
    {transaction.transactionHash ? (
      <Tooltip title={transaction.transactionHash}>
        <TransactionExplorerLink
          href={getSuiExplorerTransactionUrl(transaction.transactionHash)}
          target="_blank"
          rel="noopener noreferrer"
          size="mini"
          aria-label={`View transaction ${transaction.transactionHash} on Sui Explorer`}
          onClick={(event) => event.stopPropagation()}
        >
          <TransactionLinkText>
            {truncateAddress(transaction.transactionHash, 4, 4)}
          </TransactionLinkText>
          <TransactionExplorerIcon width={12} height={12} aria-hidden="true" />
        </TransactionExplorerLink>
      </Tooltip>
    ) : null}
    {transaction.notes ? (
      <Tooltip title={transaction.notes}>
        <NotesTrigger role="img" aria-label={`Notes: ${transaction.notes}`}>
          <NotesIcon width={24} height={24} aria-hidden="true" />
        </NotesTrigger>
      </Tooltip>
    ) : null}
    <Tooltip title="View details">
      <IconButton
        variant="ghost"
        color="tertiary"
        size="xs"
        aria-label={`View ${formatTransactionType(transaction.type)} transaction details`}
        onClick={(event) => {
          event.stopPropagation();
          onSelect(transaction.id);
        }}
      >
        <ViewIcon aria-hidden="true" />
      </IconButton>
    </Tooltip>
  </Stack>
);

interface TransactionCellProps {
  transaction: TransactionListItem;
}

interface TransactionLineProps {
  line: TransactionListLine;
  variant: SubLineVariant;
}
